import {memoize, shallowMemoize} from '@sail/utils';
import {NavigationError} from 'src/errors';
import addCarryOverIdentifiers from 'src/internal/addCarryOverIdentifiers';
import carryOverIdentificator from 'src/internal/carryOverIdentificator';
import toTree from 'src/internal/compiler/toTree';
import tokenizePath from 'src/internal/compiler/tokenizePath';
import fullLocationToUrl from 'src/internal/fullLocationToUrl';
import groupSeparator from 'src/internal/groupSeparator';
import memoizeConfig from 'src/internal/memoizeConfig';
import replaceGlobal from 'src/internal/replaceGlobal';
import stripTrailingSlash from 'src/internal/stripTrailingSlash';

import type {MetaInfo} from 'src/internal/compiler/tokenizePath';
import type {
  BaseParamsLocation,
  BaseParamsPathToRegexp,
  NavigationConfigInternal,
  Param,
  Route,
  RouteNames,
  RouteParameter,
} from 'src/types';

export const parsePath = memoize((path: string) => {
  return addCarryOverIdentifiers(replaceGlobal(stripTrailingSlash(path)));
});

const getRegexFromRoute = memoizeConfig(
  (config: NavigationConfigInternal, name: string) => {
    const routeEntry = config.routes[name];
    return parsePath(
      typeof routeEntry === 'string' ? routeEntry : routeEntry[0],
    );
  },
);

const checkValidValue = shallowMemoize(
  (id: string, value: Param, pattern: RegExp | undefined) => {
    if (!pattern) {
      if (
        id.endsWith('+') &&
        (!value || (Array.isArray(value) && value.length === 0))
      ) {
        throw new TypeError(
          `Cannot generate the param ${id} with missing param: ${pattern}`,
        );
      }
      return;
    }

    if (typeof value !== 'string' || !pattern.test(value)) {
      throw new TypeError(
        `Cannot generate the param ${id} with missing param: ${pattern}`,
      );
    }
  },
);

const valueToString = shallowMemoize((value: string | string[]) => {
  return typeof value === 'string'
    ? encodeURIComponent(value)
    : value.map(encodeURIComponent).join('/');
});

const optionalChars = new Set(['?', '!', '*']);

const isOptionalParam = shallowMemoize((idParam: string) => {
  return optionalChars.has(idParam.charAt(idParam.length - 1));
});

const modifierChars = new Set(['*', '+', '?']);

const stripModifier = shallowMemoize((id: string) => {
  const lastChar = id.charAt(id.length - 1);
  return modifierChars.has(lastChar) ? id.slice(0, -1) : id;
});

function processPathParams(
  part: string,
  params: BaseParamsPathToRegexp,
  meta: MetaInfo,
): string {
  if (!meta) {
    return part;
  }

  const idAndPattern = meta.idAndPattern;
  const id = meta.id;
  const pattern = meta.pattern;

  const value = params[stripModifier(id)] || '';

  if (!value && !isOptionalParam(id)) {
    if (!meta.isOptional) {
      throw new TypeError(
        `Missing value for the mandatory param "${id}" in the path`,
      );
    }

    return '';
  }

  if (value) {
    checkValidValue(id, value, pattern);
  }

  return part.replace(idAndPattern, valueToString(value));
}

export function tokensToPath(
  tokens: ReturnType<typeof tokenizePath>,
  params: BaseParamsPathToRegexp,
): string {
  let resultPath = '';
  for (let i = 0; i < tokens.length; i++) {
    const [token, meta] = tokens[i];

    const pathResult = processPathParams(token, params, meta);

    if (pathResult) {
      resultPath += `/${pathResult}`;
    }
  }

  return resultPath;
}

function routeToUrl<T extends RouteNames>(
  config: NavigationConfigInternal,
  route: RouteParameter<T> | Route,
  base: Required<Route> | null = null,
): string {
  const paramsPathToRegexp: BaseParamsPathToRegexp = {};
  const {
    name,
    params = {},
    search,
    searchSafe,
    hash = '',
  } = config.unsafePreProcessRoute
    ? config.unsafePreProcessRoute(route, base)
    : route;

  const setParams: (
    params: BaseParamsLocation,
    options: {carryOver: boolean},
  ) => void = (params, {carryOver}) => {
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const value = params[key];

        const keyWithMaybeCarryOverIdentificator = `${
          carryOver ? carryOverIdentificator : ''
        }${key}`;

        if (Array.isArray(value) || typeof value !== 'object') {
          paramsPathToRegexp[keyWithMaybeCarryOverIdentificator] = value;
        } else {
          for (const sub in value) {
            if (value.hasOwnProperty(sub)) {
              paramsPathToRegexp[
                `${keyWithMaybeCarryOverIdentificator}${groupSeparator}${sub}`
              ] = value[sub];
            }
          }
        }
      }
    }
  };

  let pathname;

  if (!config.routes.hasOwnProperty(name)) {
    throw new ReferenceError(`Invalid route name passed: ${name}`);
  }

  try {
    if (base) {
      // copy over current route params as carry over params
      const baseParams: BaseParamsLocation = (base.params ||
        {}) as BaseParamsLocation;

      setParams(baseParams, {carryOver: true});
    }

    const paramsLocation: BaseParamsLocation = params as BaseParamsLocation;

    // copy over new route params as carry over params
    setParams(paramsLocation, {carryOver: true});

    // set new route params as not carry over params
    setParams(paramsLocation, {carryOver: false});

    const path = getRegexFromRoute(config, name);
    const tree = toTree(config.routes);
    const tokens = tree?.t[name as RouteNames] ?? tokenizePath(path);

    pathname = tokensToPath(tokens, paramsPathToRegexp);

    // this a edge case scenario where ROOT would return an empty string.
    if (pathname === '') {
      pathname = '/';
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    throw new NavigationError(
      `Cannot generate the route ${name} with given params`,
    );
  }

  if (!pathname) {
    throw new NavigationError(
      `No path was generated for route ${name} with given params`,
    );
  }

  const url = fullLocationToUrl({
    pathname,
    search: search ?? searchSafe,
    hash,
  });

  return config.postProcessUrl ? config.postProcessUrl(url) : url;
}

export default memoizeConfig(routeToUrl);
